import { Points } from 'components/minute/builders/v2/Points';
import { ICoordinates } from 'libs/types/map/mapTypes';
import { MapMouseEvent } from 'mapbox-gl';
import { useContext, useEffect, useState } from 'react';
import { ILineV2WorldMarker, IWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    shouldBeClosedPolygon: boolean;
    updateMarkerDirectly: (markerId: string, coordinates: ICoordinates[]) => void;
    createWorldMarker: (coordinate: ICoordinates) => { markerId: string };
    onFinished?: (marker: IWorldMarker) => void;
}

export const Builder = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const mapRef = ctxEdit.mapRef.current;

    const [markerId, setMarkerId] = useState<string | null>(null);
    const marker = ctxEdit.markerCreation.marker as ILineV2WorldMarker;

    const [coordinates, setCoordinates] = useState<ICoordinates[]>([]);

    const minPoints = props.shouldBeClosedPolygon ? 3 : 2;

    useUpdateEffect(() => {
        if (!coordinates.length) {
            setCoordinates([marker.elementData.coordinates[0]]);
        }
    }, [marker]);

    const onMouseClick = (e: MapMouseEvent) => {
        const newCoordinate = { lat: e.lngLat.lat, lng: e.lngLat.lng };
        if (marker) {
            setCoordinates((curr) => [...curr, newCoordinate]);
        } else {
            const { markerId } = props.createWorldMarker(newCoordinate);
            setMarkerId(markerId);
        }
    };

    const onMouseMove = (e: MapMouseEvent) => {
        if (marker) {
            const newCoordinate = { lat: e.lngLat.lat, lng: e.lngLat.lng };
            const newCoordinates = [...coordinates, newCoordinate];
            props.updateMarkerDirectly(markerId, newCoordinates);
        }
    };

    useEffect(() => {
        if (mapRef) {
            mapRef.getCanvas().style.cursor = 'pointer';
            mapRef.on('click', onMouseClick);
            mapRef.on('mousemove', onMouseMove);
        }

        if (markerId && !ctxEdit.markerCreation) {
            mapRef.getCanvas().style.cursor = 'default';
            mapRef?.off('click', onMouseClick);
            mapRef?.off('mousemove', onMouseMove);
        }

        return () => {
            mapRef.getCanvas().style.cursor = 'default';
            mapRef?.off('click', onMouseClick);
            mapRef?.off('mousemove', onMouseMove);
        };
    }, [mapRef, marker, coordinates.length, ctxEdit.markerCreation.marker]);

    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            ctxEdit.markerCreation.remove();
            e.preventDefault();
            e.stopPropagation();
        } else if (e.key === 'Enter') {
            if (coordinates.length >= minPoints) {
                const finishedMarker = {
                    ...marker,
                    elementData: { ...marker.elementData, coordinates }
                };
                props.onFinished?.(finishedMarker);
            }
            e.preventDefault();
            e.stopPropagation();
        }
    };

    useEffect(() => {
        if (mapRef) {
            window.addEventListener('keydown', onKey);
        }

        return () => {
            window.removeEventListener('keydown', onKey);
        };
    });

    if (!ctxEdit.markerCreation.marker) {
        return null;
    }

    if (coordinates.length < 2) {
        return null;
    }

    return (
        <Points
            mapRef={ctxEdit.mapRef}
            coordinates={coordinates}
            pointsVisible={coordinates.length >= minPoints}
            pointsToShow={props.shouldBeClosedPolygon ? 'first' : 'all'}
            onPointClicked={(coordinate, isLast) => {
                function finish() {
                    const finishedMarker = {
                        ...marker,
                        elementData: { ...marker.elementData, coordinates }
                    };
                    props.onFinished?.(finishedMarker);
                }

                function addPointLocally() {
                    const newCoordinates = [...coordinates, coordinate];
                    props.updateMarkerDirectly(marker.id, newCoordinates);
                    setCoordinates(newCoordinates);
                }

                function submitCurrentCoordinates() {
                    props.updateMarkerDirectly(marker.id, coordinates);
                }

                if (props.shouldBeClosedPolygon) {
                    submitCurrentCoordinates();
                    finish();
                    return;
                }

                if (isLast) {
                    finish();
                } else {
                    addPointLocally();
                }
            }}
        />
    );
};
